import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import _ from 'underscore';
import { useAngularService } from 'helpers/get_angular_service';
import useGym from 'helpers/use_gym';
import plural from 'helpers/plural';
import api from 'helpers/api';
import sources from 'helpers/sources';
import notification from 'helpers/notification';
import redirect from 'helpers/redirect';
import { isDateInPast, isDateBeforeReference } from 'helpers/date_util';
import CancellationStepOne from './cancellation_step_one';
import CancellationStepTwo from './cancellation_step_two';
import type {
  CancellationPolicy,
  MembershipCancellation,
  CancellationReason,
  MembershipWithCancellation,
  ContactWithMemberships,
} from 'react/types/membership_cancellation';

const MembershipCancellationForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [contact, setContact] = useState<ContactWithMemberships | null>(null);
  const [membership, setMembership] = useState<MembershipWithCancellation | null>(null);
  const [cancellationPolicy, setCancellationPolicy] = useState<Partial<CancellationPolicy>>({});
  const [cancellationFeePresent, setCancellationFeePresent] = useState(false);
  const [reasons, setReasons] = useState<CancellationReason[]>([]);
  const [recurringBookings, setRecurringBookings] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, unknown>>({});

  const $stateParams = useAngularService('$stateParams');
  const ReactModal = useAngularService('ReactModal');
  const MessageService = useAngularService('MessageService');
  const loadingAnimation = useAngularService('loadingAnimation');

  const gym = useGym();

  const [cancellationData, setCancellationData] = useState<MembershipCancellation>({
    cancel_on: new Date(),
    change_payments: 'delete_all_payments',
    reason: '',
  });

  const currency = gym?.currency_symbol || '$';

  const minDate = membership?.start_date
    ? moment(membership.start_date).add(1, 'days').toDate()
    : new Date();
  const maxDate = gym?.today ? moment(gym.today).add(364, 'days').toDate() : new Date();
  const feeMinDate = gym?.today ? moment(gym.today).toDate() : new Date();

  const getStrategyText = (policy: CancellationPolicy): string => {
    switch (policy.mobile_app_cancel_fee_default_due_date_strategy) {
      case 'bundle_cancel_fee_with_final_payment_or_on_cancel_date':
        return 'charged with the final membership payment (or on the cancellation date, if there is no final payment)';
      case 'use_custom_rule_for_cancel_fee_due_date': {
        const num = policy.mobile_app_cancel_fee_default_due_date_number;
        const unit = policy.mobile_app_cancel_fee_default_due_date_unit;
        const multiplier = policy.mobile_app_cancel_fee_default_due_date_multiplier;
        const anchor = policy.mobile_app_cancel_fee_default_due_date_anchor;

        const anchorMap: Record<string, string> = {
          schedule_cancel_fee_relative_to_request_approval: 'cancellation approved',
          schedule_cancel_fee_relative_to_cancellation_date: 'cancellation date',
          schedule_cancel_fee_relative_to_final_payment: 'final payment date',
        };

        const multiplierMap: Record<string, string> = {
          schedule_cancel_fee_multiplier_before: 'before',
          schedule_cancel_fee_multiplier_after: 'after',
          schedule_cancel_fee_multiplier_on: 'on',
        };

        const anchorText = anchorMap[anchor || ''] || '';
        const multiplierText = multiplierMap[multiplier || ''] || '';

        if (multiplier === 'schedule_cancel_fee_multiplier_on') {
          return `charged on ${anchorText}`;
        }
        return `charged ${num} ${unit} ${multiplierText} ${anchorText}`;
      }
      default:
        return 'charged according to the cancellation policy';
    }
  };

  const showCancelMembershipWarning = (): void => {
    MessageService.message({
      title: `${contact?.name} - ${membership?.label}`,
      message: `
        <div class="filters">
          <p><strong>Please be aware of the following when cancelling a membership:</strong></p>
          <ol>
            <li>Once you've added a cancellation, the cancellation will be 'pending' until the membership's new expiration date. Pending cancellations can be removed or edited later, if required.</li>
            <li>Removing a pending cancellation will revert the membership and payments to their previous state, and remove any cancellation fees that were added.</li>
            <li>You cannot add a cancellation date that is within a suspension period.</li>
            <li>This page cannot be used to extend a membership's expiration period.</li>
          </ol>
        </div>
      `,
      bindHtml: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Close',
    });
  };

  const isFirstStepReady = (): boolean => {
    return !!(
      cancellationData.cancel_on &&
      isDateBeforeReference(
        cancellationData.cancel_on,
        membership?.expiration_date,
        !!membership?.membership_cancellation?.id,
      )
    );
  };

  const isSecondStepReady = (): boolean => {
    return !!((cancellationData.cancellation_fee || !cancellationFeePresent) && !isLoading);
  };

  const handleContinue = (): void => {
    setCurrentStep(2);
  };

  const goToFirstStep = (): void => {
    setCurrentStep(1);
  };

  const handleCancellationFeeToggle = (checked: boolean): void => {
    setCancellationFeePresent(checked);
    if (!checked) {
      setCancellationData((prev) => ({
        ...prev,
        cancellation_fee: 0,
        cancellation_fee_due_on: undefined,
      }));
    }
  };

  const manageOptions = (): void => {
    ReactModal.open({
      component: 'ManageMembershipCancellationReasonsModal',
    }).finally(() => {
      api.get(sources.membershipCancellationReasons(gym.id), { count: 1000 }).then((collection: CancellationReason[]) => {
        const allReasons = [...collection, { id: 'other', name: 'Other reason' }];
        setReasons(allReasons);
      });
    });
  };

  const deactivateRecurringBooking = (booking): void => {
    setRecurringBookings((prev) =>
      prev.map((b) => (b.id === booking.id ? { ...b, loading: true } : b)),
    );

    api.delete(sources.recurringBooking(gym.id, booking.id)).then(
      () => {
        setRecurringBookings((prev) =>
          prev.map((b) => (b.id === booking.id ? { ...b, loading: false, deactivated: true } : b)),
        );
      },
      () => {
        setRecurringBookings((prev) =>
          prev.map((b) => (b.id === booking.id ? { ...b, loading: false } : b)),
        );
        notification.error('There was an error processing the request.');
      },
    );
  };

  const handleSave = (): void => {
    if (!gym?.id || !contact?.id || !membership?.id) return;

    setIsLoading(true);
    setErrors({});

    interface SaveParams {
      membership_id?: number;
      membership_cancellation: MembershipCancellation;
    }

    const requestData: SaveParams = {
      membership_id: membership.id,
      membership_cancellation: { ...cancellationData },
    };

    if (requestData.membership_cancellation.membership_cancellation_reason_id === 'other') {
      requestData.membership_cancellation.membership_cancellation_reason_id = null;
    }

    const fee = requestData.membership_cancellation.cancellation_fee;
    if (!fee || fee === 0) {
      delete requestData.membership_cancellation.cancellation_fee_due_on;
      requestData.membership_cancellation.cancellation_fee = undefined;
    }

    const cancellationId = membership.membership_cancellation?.id;
    const contactType = $stateParams.contactType;

    const saveRequest = cancellationId
      ? api.put(
          sources.membershipCancellation(gym.id, contactType, contact.id, cancellationId),
          requestData,
        )
      : api.post(sources.membershipCancellations(gym.id, contactType, contact.id), requestData);

    saveRequest.then(
      () => {
        notification.success('Cancellation successfully saved');
        if ($stateParams.membershipReplacementId) {
          redirect('gym.contacts.membership', {
            contactId: contact.id,
            membershipReplacementId: $stateParams.membershipReplacementId,
            targetMembershipId: $stateParams.targetMembershipId,
          });
        } else {
          redirect('gym.contacts.edit', { contactId: contact.id });
        }
      },
      (response) => {
        setIsLoading(false);
        setErrors(response.data?.errors || response.data || {});
        notification.error('An error occurred while adding the cancellation');
        if (response.data?.errors?.cancellation?.cancel_on) {
          goToFirstStep();
        }
      },
    );
  };

  const handleCancel = (): void => {
    redirect('gym.contacts.edit', { contactId: contact?.id });
  };

  useEffect(() => {
    if (!gym?.id || !$stateParams.contactId || !$stateParams.contactType) return;

    api.get(sources.contactWithType({
      gymId: gym.id,
      contactType: $stateParams.contactType,
      contactId: $stateParams.contactId,
    })).then((contactData: ContactWithMemberships) => {
      setContact(contactData);

      let membershipData: MembershipWithCancellation | undefined;

      if ($stateParams.membershipId) {
        membershipData = _.find(
          contactData.memberships,
          (m: MembershipWithCancellation) => m.id === parseInt($stateParams.membershipId, 10),
        );
      } else if ($stateParams.cancellationId) {
        membershipData = _.find(
          contactData.memberships,
          (m: MembershipWithCancellation) =>
            m.membership_cancellation?.id === parseInt($stateParams.cancellationId, 10),
        );
      }

      setMembership(membershipData);

      if (!membershipData?.id) {
        notification.error('Unable to find membership');
        redirect('gym.contacts.edit', { contactId: contactData.id });
      }
    });
  }, [gym?.id, $stateParams.contactId, $stateParams.contactType, $stateParams.membershipId, $stateParams.cancellationId]);

  useEffect(() => {
    if (!gym?.id || !contact?.id || !membership?.id || !$stateParams.contactType) return;

    api.get(sources.newMembershipCancellation(gym.id, $stateParams.contactType, contact.id), { membership_id: membership.id }).then(
      (response: { cancellation_policy: CancellationPolicy }) => {
        const policy = response.cancellation_policy;

        const policyCopy: Partial<CancellationPolicy> = { ...policy };
        policyCopy.notice_unit_label = plural(
          policy.mobile_app_cancellation_notice_period_unit,
          policy.mobile_app_cancellation_notice_period_number,
        );
        policyCopy.term_unit_label = plural(
          policy.mobile_app_cancellation_minimum_term_unit,
          policy.mobile_app_cancellation_minimum_term_number,
        );
        policyCopy.fee_for_mobile_app_cancellations = parseFloat(
          policy.fee_for_mobile_app_cancellations?.toString() || '0',
        ).toFixed(2);
        policyCopy.fee_strategy_text = getStrategyText(policy);
        setCancellationPolicy(policyCopy);

        if (!membership?.membership_cancellation?.id) {
          const cancelOn = policyCopy.minimum_cancellation_date || moment.tz(gym.tz).toDate();
          const changePayments =
            policy.mobile_app_cancellation_payments_policy ===
            'cancellation_payments_policy_delete_payments_after_cancellation'
              ? 'delete_payments_after_cancellation'
              : 'delete_all_payments';

          setCancellationData((prev) => ({
            ...prev,
            cancel_on: cancelOn,
            change_payments: changePayments,
          }));
        }
      },
      () => {
        notification.error('Unable to load membership cancellation policy');
      },
    );
  }, [gym?.id, contact?.id, membership?.id, $stateParams.contactType]);

  useEffect(() => {
    if (!membership) return;

    const cancelOn = $stateParams.membershipReplacementId ? gym?.today : undefined;
    const defaultReason = $stateParams.reason || '';

    if (membership.membership_cancellation?.id) {
      const cancellation = membership.membership_cancellation;
      const reasonId = cancellation.membership_cancellation_reason_id;
      const cancellationFee =
        typeof cancellation.cancellation_fee === 'string'
          ? parseFloat(cancellation.cancellation_fee)
          : cancellation.cancellation_fee;

      setCancellationData({
        cancel_on: cancellation.cancel_on,
        membership_cancellation_reason_id:
          cancellation.reason && !reasonId ? 'other' : reasonId ?? undefined,
        reason: reasonId ? '' : cancellation.reason,
        change_payments: cancellation.change_payments,
        cancellation_fee: cancellationFee,
        cancellation_fee_due_on: cancellation.cancellation_fee_due_on,
      });

      if (cancellationFee > 0) {
        setCancellationFeePresent(true);
      }
    } else {
      setCancellationData((prev) => ({
        ...prev,
        reason: defaultReason,
        cancel_on: prev.cancel_on || cancelOn,
      }));
    }

    if ($stateParams.membershipReplacementId) {
      setCancellationData((prev) => ({
        ...prev,
        pending_membership_replacement_id: +$stateParams.membershipReplacementId,
      }));
    }
  }, [membership, $stateParams.reason, $stateParams.membershipReplacementId]);

  useEffect(() => {
    if (!gym?.id || !membership?.id) return;

    const params = {
      count: 1000,
      'filters[membership_id]': membership.id,
    };

    api.get(sources.recurringBookings(gym.id), params).then((collection: any[]) => {
      setRecurringBookings(collection);
    });
  }, [gym?.id, membership?.id]);

  useEffect(() => {
    if (!gym?.id || !membership) return;

    api.get(sources.membershipCancellationReasons(gym.id), { count: 1000 }).then((collection: CancellationReason[]) => {
      const allReasons = [...collection, { id: 'other', name: 'Other reason' }];
      setReasons(allReasons);

      const reasonIdToCheck = membership.membership_cancellation?.membership_cancellation_reason_id;
      if (reasonIdToCheck) {
        const appliedReason = _.find(
          collection,
          (reason: CancellationReason) => reason.id === reasonIdToCheck,
        );
        if (!appliedReason) {
          setCancellationData((prev) => ({
            ...prev,
            membership_cancellation_reason_id: 'other',
            reason: membership.membership_cancellation?.reason || prev.reason,
          }));
        }
      }
    });
  }, [gym?.id, membership]);

  useEffect(() => {
    if (!contact || !membership) {
      loadingAnimation.show();
    } else {
      loadingAnimation.hide();
    }
    return () => loadingAnimation.hide();
  }, [contact, membership]);

  if (!contact || !membership) {
    return null;
  }

  return (
    <div className="content-section">
      <div className="container">
        <header className="flex cancel-membership-header">
          <h2 className="worx-combo-box-headline">Cancel Membership</h2>
          <button className="btn btn-link align-center" onClick={showCancelMembershipWarning}>
            <i className="icon-question ir-suspension-question-icon" />
            Learn more about cancelling memberships
          </button>
        </header>

        {currentStep === 1 && (
          <CancellationStepOne
            contact={contact}
            membership={membership}
            cancellationPolicy={cancellationPolicy}
            currency={currency}
            cancellationData={cancellationData}
            reasons={reasons}
            errors={errors}
            minDate={minDate}
            maxDate={maxDate}
            onCancellationDataChange={setCancellationData}
            onManageOptions={manageOptions}
            onContinue={handleContinue}
            onCancel={handleCancel}
            isCancellationDateValid={isDateBeforeReference(
              cancellationData.cancel_on,
              membership?.expiration_date,
              !!membership?.membership_cancellation?.id,
            )}
            isFirstStepReady={isFirstStepReady()}
          />
        )}

        {currentStep === 2 && (
          <CancellationStepTwo
            contact={contact}
            membership={membership}
            currency={currency}
            cancellationData={cancellationData}
            cancellationFeePresent={cancellationFeePresent}
            recurringBookings={recurringBookings}
            errors={errors}
            feeMinDate={feeMinDate}
            maxDate={maxDate}
            onCancellationDataChange={setCancellationData}
            onCancellationFeeToggle={handleCancellationFeeToggle}
            onDeactivateRecurringBooking={deactivateRecurringBooking}
            onSave={handleSave}
            onBack={goToFirstStep}
            isSecondStepReady={isSecondStepReady()}
            isCancellationInThePast={isDateInPast(cancellationData.cancel_on, gym?.tz)}
          />
        )}
      </div>
    </div>
  );
};

export default MembershipCancellationForm;
